import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import qrDialogBackground from "../assets/qr-dialog-background.png";
import attachment from "../assets/attachment.png";

const DialogOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 99;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;

  @media (max-width: 768px) {
    display: none; /* Hide desktop overlay on mobile */
  }
`;

const Dialog = styled(motion.div)`
  position: relative;
  background: white url(${qrDialogBackground}) no-repeat center center;
  background-size: cover;
  padding: 3rem;
  border-radius: 24px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  will-change: transform, opacity;
  backface-visibility: hidden;
  pointer-events: auto;
`;

const HitchhikerImage = styled.img`
  position: absolute;
  right: 20px;
  top: -5%;
  transform: rotate(5deg) translateZ(0);
  width: 220px;
  height: auto;
  z-index: 102;
  will-change: transform;
  backface-visibility: hidden;
`;

const DialogContent = styled.div`
  max-width: 55%;
  position: relative;
  z-index: 2;
  transform: rotate(5deg);
  padding-left: 2rem;
  margin-top: 40px;

  h2 {
    font-size: 36px;
    font-weight: 900;
    margin-bottom: 1.5rem;
    font-style: italic;
    color: #000;
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-family: "Inter", sans-serif;
  }

  p {
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    color: rgba(0, 0, 0, 0.8);
    font-weight: 500;
    transform: rotate(1deg);
    margin-left: 1rem;
    font-family: "Inter", sans-serif;
  }
`;

const AttachmentImage = styled.img`
  position: absolute;
  left: 24px;
  top: 24px;
  width: 24px;
  height: auto;
  opacity: 0.4;
`;

const DialogWrapper = styled.div`
  position: fixed;
  top: 45%;
  left: 45%;
  transform: translate(-45%, -45%) rotate(355deg);
  z-index: 100;
  width: 800px;
  height: 460px;
  max-width: calc(90vw - 64px);
  margin-bottom: 40px;
  margin-right: 40px;
  pointer-events: none;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -44px;
  left: -44px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  width: 48px;
  height: 48px;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  z-index: 103;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  pointer-events: auto;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

// Mobile-specific components for the Hitchhiker dialog
const HitchhikerBottomSheetOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
`;

const HitchhikerBottomSheet = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  z-index: 1000;
  padding: 2rem 1.5rem 3rem 1.5rem;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.2);
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  will-change: transform;
  backface-visibility: hidden;
  contain: layout style paint;
  touch-action: pan-y;
  transform: translate3d(0, 0, 0);
`;

const HitchhikerHandleBar = styled.div`
  width: 40px;
  height: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  margin: 0 auto 1.5rem auto;
`;

const HitchhikerContent = styled.div`
  text-align: center;
  padding: 0 1rem;
  width: 100%;
  box-sizing: border-box;

  h2 {
    font-size: 24px;
    font-weight: 900;
    margin-bottom: 1rem;
    font-style: italic;
    color: #000;
    line-height: 1.2;
    letter-spacing: -0.02em;
    font-family: "Inter", sans-serif;

    @media (max-width: 480px) {
      font-size: 20px;
    }

    @media (max-width: 360px) {
      font-size: 18px;
    }
  }

  p {
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 1.5rem;
    color: rgba(0, 0, 0, 0.8);
    font-weight: 500;
    font-family: "Inter", sans-serif;

    @media (max-width: 480px) {
      font-size: 13px;
    }

    @media (max-width: 360px) {
      font-size: 12px;
    }
  }
`;

const HitchhikerImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

const HitchhikerMobileImage = styled.img`
  width: 180px;
  height: auto;
  max-width: 100%;

  @media (max-width: 480px) {
    width: 160px;
  }

  @media (max-width: 360px) {
    width: 140px;
  }
`;

const HitchhikerCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  z-index: 1001;
  pointer-events: auto;
  will-change: transform;
  transform: translateZ(0);

  &:hover {
    background: rgba(0, 0, 0, 0.15);
  }

  &:active {
    background: rgba(0, 0, 0, 0.2);
    transform: translateZ(0) scale(0.95);
  }
`;

// Define animation variants outside component to prevent re-creation
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const sheetVariants = {
  hidden: {
    y: "100%",
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.4,
    },
  },
  visible: {
    y: 0,
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.5,
    },
  },
  exit: {
    y: "100%",
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.3,
    },
  },
};

// Define desktop animation variants outside component
const desktopOverlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const desktopDialogVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.3,
    },
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      type: "tween",
      ease: [0.32, 0.72, 0, 1],
      duration: 0.2,
    },
  },
};

const HitchhikerMobileBottomSheet = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <HitchhikerBottomSheetOverlay
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={onClose}
          />
          <HitchhikerBottomSheet
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={e => e.stopPropagation()}
          >
            <HitchhikerHandleBar />
            <HitchhikerCloseButton onClick={onClose}>×</HitchhikerCloseButton>
            <HitchhikerContent>
              <h2>THE HITCHHIKER'S GUIDE TO THE GALAXY</h2>
              <p>
                In The Hitchhiker's Guide to the Galaxy, "42" is the "Answer to
                the Ultimate Question of Life, the Universe, and Everything,"
                calculated by the supercomputer Deep Thought over 7.5 million
                years. However, the actual question is unknown, making the
                answer meaningless. Douglas Adams, the author, later explained
                that he chose 42 simply because it was a "funny, ordinary
                number" with no deeper significance.
              </p>
              <HitchhikerImageContainer>
                <HitchhikerMobileImage
                  src="/hitchhiker.png"
                  alt="Hitchhiker's Guide"
                />
              </HitchhikerImageContainer>
            </HitchhikerContent>
          </HitchhikerBottomSheet>
        </>
      )}
    </AnimatePresence>
  );
};

const HitchhikerDialog = ({ isOpen, onClose }) => {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize with correct value to prevent hydration mismatch
    return typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  });

  useEffect(() => {
    const checkMobile = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(prev => (prev !== newIsMobile ? newIsMobile : prev));
    };

    // Only add listener if needed and debounce resize events
    let timeoutId;
    const debouncedCheckMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 100);
    };

    window.addEventListener("resize", debouncedCheckMobile);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedCheckMobile);
    };
  }, []);

  return (
    <>
      {/* Desktop Dialog */}
      <AnimatePresence mode="wait">
        {isOpen && !isMobile && (
          <>
            <DialogOverlay
              variants={desktopOverlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={onClose}
            />
            <DialogWrapper
              as={motion.div}
              variants={desktopDialogVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Dialog>
                <AttachmentImage src={attachment} alt="" />
                <DialogContent>
                  <h2>THE HITCHHIKER'S GUIDE TO THE GALAXY</h2>
                  <p>
                    In The Hitchhiker's Guide to the Galaxy, "42" is the "Answer
                    to the Ultimate Question of Life, the Universe, and
                    Everything," calculated by the supercomputer Deep Thought
                    over 7.5 million years. However, the actual question is
                    unknown, making the answer meaningless. Douglas Adams, the
                    author, later explained that he chose 42 simply because it
                    was a "funny, ordinary number" with no deeper significance.
                  </p>
                </DialogContent>
              </Dialog>
              <CloseButton onClick={onClose}>×</CloseButton>
              <HitchhikerImage src="/hitchhiker.png" alt="Hitchhiker's Guide" />
            </DialogWrapper>
          </>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Sheet */}
      {isMobile && (
        <HitchhikerMobileBottomSheet isOpen={isOpen} onClose={onClose} />
      )}
    </>
  );
};

export default HitchhikerDialog;
